/**
 * React Query Hook: Get Default Risk Matrix Data
 *
 * Hook for fetching default matrix structure from backend for diff comparison.
 * Endpoint: GET /api/load-default-matrix
 */

import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { loadDefaultMatrix } from "@/api/endpoints/riskAnalysis";
import { queryKeys } from "@/lib/queryClient";
import { RiskMatrixData } from "../../../types/risk-analysis";

/**
 * Hook to get default matrix data for a specific session and sector
 *
 * Features:
 * - Only fetches when enabled=true (controlled by isDefault flag)
 * - Automatically cached for 5 minutes (stale time)
 * - Used for comparing saved data against defaults
 * - Shared across components using this hook
 * - Request deduplication
 *
 * @param sessionId - The session ID (required for security validation)
 * @param sectorId - The sector ID to fetch defaults for (required)
 * @param enabled - Whether to fetch (set to false when isDefault=true)
 * @param config - Optional React Query configuration
 * @returns React Query result with default matrix data
 *
 * @example
 * // Only fetch when user has saved data (not defaults)
 * const { data: defaultMatrixData, isLoading } = useGetDefaultMatrix(
 *   sessionId,
 *   sectorId,
 *   riskMatrixData?.isDefault === false  // Only when user has saved changes
 * );
 */
export function useGetDefaultMatrix(
  sessionId: string | null | undefined,
  sectorId: string | undefined,
  enabled: boolean = true,
  config?: Omit<UseQueryOptions<RiskMatrixData, Error>, "queryKey" | "queryFn">
): UseQueryResult<RiskMatrixData, Error> {
  return useQuery({
    queryKey: queryKeys.riskAnalysis.defaults(sessionId || "", sectorId || ""),
    queryFn: () => {
      if (!sessionId) {
        throw new Error("Session ID is required");
      }
      if (!sectorId) {
        throw new Error("Sector ID is required");
      }
      return loadDefaultMatrix({ session_id: sessionId, sector_id: sectorId });
    },
    enabled: !!sessionId && !!sectorId && enabled && config?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes - defaults rarely change
    ...config,
  });
}
